import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { getSurfaceColorProps } from "../../utils/colorValue";
import { collection, getDocs, query, where } from "firebase/firestore";
import { useNavigate, useParams } from "react-router-dom";
import { db } from "../../firebaseConfig";
import { usePublicPageMetadata } from "../../hooks/usePublicPageMetadata";
import { NotFoundPage, PageUnavailable } from "./PageStatus";
import { renderPublicMuultiComponent } from "../renderPublicMuultiComponent";
import { choosePreferredUserSnapshot } from "../../../functions/publicMetadataPolicy.js";
import { mapWallSection } from "../../utils/muulti3dSurface";
import { buildRoomLayout, MUULTI_GREEN, PANEL_WIDTH, PANEL_HTML_DISTANCE_FACTOR, ROOM_HEIGHT, getPanelFrameHeight, getRoomHeight, resolveSurface } from "../../utils/muulti3dLayout";
import { QUALITY_PRESETS, getScenePanelTextStyle, SceneDialog, SceneHtml, ScenePanelBody, ScenePanelContent, SceneText, SceneViewport, SceneWallBand, updateSurfaceMaterial, useNearbyIndices, useSceneLighting, useSceneQuality, useSurfaceTexture } from "./Muulti3DShared";
import "../MuultiStyle.css";
import "./Muulti3D.css";

const spaceRequests = new Map();
function readSpace(username) {
    if (!spaceRequests.has(username)) {
        const request = getDocs(query(collection(db, "users"), where("muultiName", "==", username)))
            .then(snapshot => {
                const doc = choosePreferredUserSnapshot(snapshot.docs);
                return doc ? { ...doc.data(), id: doc.id } : null;
            }).finally(() => spaceRequests.delete(username));
        spaceRequests.set(username, request);
    }
    return spaceRequests.get(username);
}

function WallSection({ name, side, height, left, right, bottom = 0, top = height, children }) {
    const geometry = useMemo(() => mapWallSection(new THREE.PlaneGeometry(right - left, top - bottom),
        side, height, left, right, bottom, top), [side, height, left, right, bottom, top]);
    useEffect(() => () => geometry.dispose(), [geometry]);
    return <mesh name={name} position={[(left + right) / 2, (bottom + top) / 2, side / 2]} rotation-y={Math.PI} geometry={geometry}>
        {children}
    </mesh>;
}

function Room({ layout, surface, texture, header, headerTexture, title, onExit }) {
    const { half, height, side, door } = layout;
    const doorX = door.position[0];
    const doorWidth = 2.8;
    const doorHeight = 4.1;
    const wallMaterial = <meshStandardMaterial onUpdate={updateSurfaceMaterial} map={texture} {...getSurfaceColorProps(surface, texture)} roughness={0.9} />;
    return <>
        <mesh rotation-x={-Math.PI / 2} receiveShadow>
            <planeGeometry args={[side, side]} /><meshStandardMaterial color={MUULTI_GREEN} roughness={0.95} />
        </mesh>
        <mesh rotation-x={Math.PI / 2} position={[0, height, 0]}>
            <planeGeometry args={[side, side]} /><meshStandardMaterial color="#254e50" roughness={1} />
        </mesh>
        <mesh name="interior-wall-back" position={[0, height / 2, -half]}>
            <planeGeometry args={[side, height]} />{wallMaterial}
        </mesh>
        <mesh name="interior-wall-right" position={[half, height / 2, 0]} rotation-y={-Math.PI / 2}>
            <planeGeometry args={[side, height]} />{wallMaterial}
        </mesh>
        <mesh name="interior-wall-left" position={[-half, height / 2, 0]} rotation-y={Math.PI / 2}>
            <planeGeometry args={[side, height]} />{wallMaterial}
        </mesh>
        {/* The front wall has a real doorway, with a door leading to the street. */}
        {[
            { left: -half, right: doorX - doorWidth / 2 },
            { left: doorX + doorWidth / 2, right: half },
        ].map(({ left, right }, index) => <WallSection key={index} name={"interior-wall-front-" + index}
            side={side} height={height} left={left} right={right}>{wallMaterial}</WallSection>)}
        <WallSection name="interior-wall-front-lintel" side={side} height={height}
            left={doorX - doorWidth / 2} right={doorX + doorWidth / 2} bottom={doorHeight}>{wallMaterial}</WallSection>
        <SceneWallBand half={half} height={height} surface={header} texture={headerTexture} inside />
        <group position={[doorX, doorHeight / 2, half - 0.1]} rotation-y={Math.PI}
            onClick={event => { event.stopPropagation(); onExit(); }}>
            <mesh><planeGeometry args={[doorWidth + 0.3, doorHeight + 0.2]} /><meshBasicMaterial color="#7ed786" /></mesh>
            <mesh position={[0, 0, 0.02]}><planeGeometry args={[doorWidth - 0.2, doorHeight - 0.1]} /><meshBasicMaterial color={MUULTI_GREEN} /></mesh>
            <SceneText position={[0, 0.6, 0.04]} fontSize={0.4} color="#ffffff">EXIT</SceneText>
            <SceneText position={[0, 0.05, 0.04]} fontSize={0.22} maxWidth={2.1} color="#ffffff">Back to street</SceneText>
            <mesh position={[0.85, -0.55, 0.04]}><circleGeometry args={[0.1, 12]} /><meshBasicMaterial color="#7ed786" /></mesh>
        </group>
        <group position={[doorX, 6.2, half - 0.12]} rotation-y={Math.PI}>
            <mesh><planeGeometry args={[Math.min(6.5, side - 4), 1.2]} /><meshBasicMaterial color={MUULTI_GREEN} /></mesh>
            <SceneText position={[0, 0, 0.02]} fontSize={0.3} maxWidth={Math.min(6, side - 5)} color="#ffffff"
                clipRect={[-3, -0.45, 3, 0.45]}>{String(title)}</SceneText>
        </group>
    </>;
}

function CeilingLights({ layout, lightingOn }) {
    const offset = layout.side * 0.25;
    const positions = [[-offset, -offset], [offset, -offset], [-offset, offset], [offset, offset]];
    return positions.map(([x, z], index) => <group key={index} name={"ceiling-fixture-" + index} position={[x, layout.height - 0.15, z]}>
        <mesh>
            <boxGeometry args={[2.8, 0.24, 1.4]} /><meshStandardMaterial color={MUULTI_GREEN} roughness={0.8} />
        </mesh>
        <mesh position={[0, -0.14, 0]}>
            <boxGeometry args={[2.5, 0.05, 1.1]} />
            <meshStandardMaterial color="#7bc2b6" emissive="#e3ffe5" emissiveIntensity={lightingOn ? 1.2 : 0} roughness={0.5} />
        </mesh>
        {lightingOn && <pointLight name={"ceiling-light-" + index} position={[0, -0.4, 0]} color="#e3ffe5"
            intensity={layout.height} distance={Math.max(layout.side, layout.height * 2)} decay={1} />}
    </group>);
}

function WallPanel({ comp, muulti, cell, index, live, onOpen, onMeasure }) {
    const panelNode = useMemo(() => live ? renderPublicMuultiComponent({ comp, muulti }) : null, [comp, muulti, live]);
    const handleMeasure = useCallback(height => onMeasure(index, height), [index, onMeasure]);
    const title = String(comp.title || comp.heading || comp.caption || comp.type || "Panel");
    const textStyle = getScenePanelTextStyle(muulti.typography, comp.style);
    return <group position={cell.position} rotation={cell.rotation}>
        <mesh position={[0, 0, -0.06]} onClick={event => { event.stopPropagation(); onOpen(); }}>
            <boxGeometry args={[PANEL_WIDTH, cell.height, 0.16]} />
            <meshStandardMaterial color="#7ed786" roughness={0.8} />
        </mesh>
        <mesh position={[0, 0, 0.03]} onClick={event => { event.stopPropagation(); onOpen(); }}>
            <planeGeometry args={[PANEL_WIDTH - 0.25, cell.height - 0.25]} /><meshBasicMaterial color="#7bc2b6" />
        </mesh>
        {live ? <SceneHtml transform position={[0, 0, 0.06]} distanceFactor={PANEL_HTML_DISTANCE_FACTOR} zIndexRange={[30, 10]}>
            <ScenePanelContent title={title} onOpen={onOpen} onMeasure={handleMeasure}
                pageTypography={muulti.typography} panelStyle={comp.style}>{panelNode}</ScenePanelContent>
        </SceneHtml> : <>
            <SceneText position={[0, 0.5, 0.06]} fontSize={0.34} maxWidth={5.7} color={textStyle["--muulti-3d-panel-heading"]}
                clipRect={[-2.9, -1.1, 2.9, 1.1]}>{title}</SceneText>
            <SceneText position={[0, -1.3, 0.06]} fontSize={0.22} color={textStyle.color}>Approach to view · select to expand</SceneText>
        </>}
    </group>;
}

function WallPanels({ components, muulti, layout, quality, playerPosRef, selectedIndex, onOpen, onMeasure }) {
    const nearby = useNearbyIndices(layout.panels, playerPosRef, quality.panelDistance, quality.panelLimit);
    return components.map((comp, index) => <WallPanel key={String(comp.id ?? index) + ":" + comp.type}
        comp={comp} muulti={muulti} cell={layout.panels[index]} live={selectedIndex == null && nearby.includes(index)}
        index={index} onMeasure={onMeasure} onOpen={() => onOpen(index)} />);
}

function IndoorSpace({ muulti }) {
    const navigate = useNavigate();
    const components = useMemo(() => (Array.isArray(muulti.components) ? muulti.components.filter(Boolean) : []), [muulti.components]);
    const [measurements, setMeasurements] = useState({ heights: [], ceiling: ROOM_HEIGHT });
    const onMeasure = useCallback((index, pixels) => {
        const height = getPanelFrameHeight(pixels);
        setMeasurements(previous => {
            if (previous.heights[index] === height) return previous;
            const heights = previous.heights.slice();
            heights[index] = height;
            return { heights, ceiling: Math.max(previous.ceiling, getRoomHeight(heights)) };
        });
    }, []);
    const layout = useMemo(() => ({
        ...buildRoomLayout(components.length, measurements.heights), height: measurements.ceiling,
    }), [components.length, measurements]);
    const surface = useMemo(() => resolveSurface(muulti.background), [muulti.background]);
    const header = useMemo(() => resolveSurface(muulti.headerBackground, "#ffffff"), [muulti.headerBackground]);
    const [qualityKey, setQualityKey] = useSceneQuality();
    const [lightingOn, setLightingOn] = useSceneLighting();
    const quality = QUALITY_PRESETS[qualityKey];
    const texture = useSurfaceTexture(surface, quality.textureSize, true, [layout.side, layout.height]);
    const headerTexture = useSurfaceTexture(header, quality.textureSize);
    const [selectedIndex, setSelectedIndex] = useState(null);
    const [panelChoice, setPanelChoice] = useState("");
    const poseRef = useRef({ ...layout.entrance });
    const playerPosRef = useRef(new THREE.Vector3(layout.entrance.x, 0, layout.entrance.z));
    const teleportRef = useRef(null);
    const open2D = useCallback(() => navigate("/" + encodeURIComponent(muulti.muultiName)), [muulti.muultiName, navigate]);
    const onExit = useCallback(() => navigate("/3d/street"), [navigate]);
    const selected = selectedIndex == null ? null : components[selectedIndex];
    return <>
        <SceneViewport label={String(muulti.pageTitle || muulti.muultiName) + " indoor Space"}
            qualityKey={qualityKey} setQualityKey={setQualityKey} showShadows={false} bounds={layout.bounds}
            lightingOn={lightingOn} setLightingOn={setLightingOn}
            playerPosRef={playerPosRef} poseRef={poseRef} teleportRef={teleportRef} paused={!!selected}
            fallbackHref={"/" + encodeURIComponent(muulti.muultiName)}
            actions={<>
                <label>Panels<select value={panelChoice} onChange={event => setPanelChoice(event.target.value)}>
                    <option value="">Choose a panel</option>
                    {components.map((comp, index) => <option key={String(comp.id ?? index)} value={index}>
                        {String(comp.title || comp.heading || comp.caption || comp.type || "Panel")}
                    </option>)}
                </select></label>
                <button className="muulti-button" disabled={panelChoice === ""} onClick={() => setSelectedIndex(Number(panelChoice))}>Open selected panel</button>
                <button className="muulti-button" onClick={onExit}>Exit to street</button>
                <button className="muulti-button" onClick={open2D}>Open 2D page</button>
            </>}
            overlay={!components.length ? <p className="muulti-3d-notice" role="status">This Space has no panels yet.</p> : null}>
            <color attach="background" args={[MUULTI_GREEN]} />
            <ambientLight intensity={lightingOn ? 0.55 : 0.18} />
            <hemisphereLight args={["#e3ffe5", MUULTI_GREEN, lightingOn ? 0.35 : 0.12]} />
            <CeilingLights layout={layout} lightingOn={lightingOn} />
            <Room layout={layout} surface={surface} texture={texture} header={header} headerTexture={headerTexture} title={muulti.pageTitle || muulti.muultiName} onExit={onExit} />
            <WallPanels components={components} muulti={muulti} layout={layout} quality={quality} playerPosRef={playerPosRef}
                selectedIndex={selectedIndex} onOpen={setSelectedIndex} onMeasure={onMeasure} />
        </SceneViewport>
        {selected && <SceneDialog title={String(selected.title || selected.heading || selected.caption || selected.type || "Panel")}
            onClose={() => setSelectedIndex(null)}>
            <ScenePanelBody pageTypography={muulti.typography} panelStyle={selected.style}>
                {renderPublicMuultiComponent({ comp: selected, muulti })}
            </ScenePanelBody>
        </SceneDialog>}
    </>;
}

export default function MuultiSpace3DDetailPage() {
    const { username } = useParams();
    const [muulti, setMuulti] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setLoadError(false);
        setMuulti(null);
        readSpace(username).then(resolved => { if (!cancelled) setMuulti(resolved); })
            .catch(() => { if (!cancelled) setLoadError(true); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [username]);
    usePublicPageMetadata(muulti, loading || loadError);
    if (loading) return <div className="muulti-container" role="status"><p>Loading 3D space...</p></div>;
    if (loadError) return <PageUnavailable />;
    if (!muulti) return <NotFoundPage />;
    return <IndoorSpace key={muulti.id + ":" + username} muulti={muulti} />;
}
